import { Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { HcToastrService } from '@manager-app/service/toastr.service';
import { ContractService } from '@manager-app/storage/service/contract.service';
import { ContractModel, ContractRequest, Currency, FileModel, Folder } from 'hx-services';
import { HxDateSelectComponent, HxEnumSelectComponent, HxErrorHandlerService, HxFileListComponent, HxFileUploadComponent } from 'hx-component';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { NgIf } from '@angular/common';
import { OrganizationSelectComponent } from '../../../components/organization-select/organization-select.component';
import { SuppliersSearchComponent } from '../../../components/suppliers-search/suppliers-search.component';
import { BankSearchComponent } from '../../bank-search/bank-search.component';

/**
 * Create and edit contracts
 */
@Component({
    selector: 'app-contract-new',
    templateUrl: './contract-new.component.html',
    imports: [NgIf, FormsModule, ReactiveFormsModule, OrganizationSelectComponent, SuppliersSearchComponent, BankSearchComponent, HxDateSelectComponent, HxEnumSelectComponent, HxFileListComponent, HxFileUploadComponent, TranslocoPipe]
})
export class ContractNewComponent implements OnInit {
  @ViewChild('templateRef') templateRef: TemplateRef<any>;
  @Output() contractSave = new EventEmitter<boolean>();
  contractForm: UntypedFormGroup;
  isLoading = {
    form: false
  };
  isEdit = false;
  fileList: FileModel[] = [];
  deletedFileList: FileModel[] = [];
  folderContract = Folder.CONTRACT;
  organizationId: number;
  currencies = Currency;

  @Input()
  set id(id) {
    this.fileList = [];
    this._id = id || null;
    if (id) {
      this.getContract();
    }
  }

  get id() {
    return this._id;
  }

  private _id: number;

  constructor(
    private modal: NgbModal,
    private fb: UntypedFormBuilder,
    private api: ContractService,
    private toastr: HcToastrService,
    private errorHandlerService: HxErrorHandlerService,
    private tr: TranslocoService
  ) {
  }

  ngOnInit() {
    this.createForm();
  }

  onFileRemoved(event: { file: FileModel, index: number }) {
    this.fileList.splice(event.index, 1);
    this.deletedFileList.push(event.file);
  }

  onFilesUpdated(event: FileModel) {
    this.fileList.push(event);
  }

  showModal() {
    this.isEdit = false;
    this.addContract();
  }

  closeModal(modal) {
    this.contractSave.emit(false);
    this.contractForm.reset();

    if (modal) {
      modal.dismiss();
    }
  }

  async onSubmitForm(modal) {
    const fileIds: number[] = this.fileList.map(r => r.id);
    const request: ContractRequest = {
      number: this.contractForm.controls['number'].value,
      date: this.contractForm.controls['date'].value,
      title: this.contractForm.controls['title'].value,
      note: this.contractForm.controls['note'].value,
      supplierId: this.contractForm.controls['supplierId'].value,
      fileIds: fileIds,
      currency: this.contractForm.controls['currency'].value,
      dueDays: this.contractForm.controls['dueDays'].value,
      organizationId: this.contractForm.controls['organizationId'].value,
      bankId: this.contractForm.controls['bankId'].value,
      iban: this.contractForm.controls['iban'].value
    };
    try {
      if (this.id) {
        await this.api.updateContract(this.id, request);
        this.toastr.success(this.tr.translate('contract-new.ts.treatySuccess'));
      } else {
        await this.api.createContract(request);
        this.toastr.success(this.tr.translate('contract-new.ts.treatyCreate'));
      }
      this.contractSave.emit(true);
      modal.dismiss();
    } catch (err: any) {
      this.errorHandlerService.check(err?.error);
    }
  }

  public updateBank(event: {id: number, text?: string}) {
    this.contractForm.controls['bankId'].patchValue(event.id);
  }

  private async getContract() {
    this.isLoading.form = true;
    setTimeout(() => {
      this.addContract();
    }, 0);
    try {
      const result = await this.api.getContract(this.id);
      if (result) {
        this.updateForm(result);
      }
    } catch (e) {
      this.toastr.error(this.tr.translate('contract-new.ts.error'));
    } finally {
      this.isLoading.form = false;
    }
  }

  private updateForm(item: ContractModel) {
    this.isEdit = true;
    this.contractForm.patchValue(item);
    this.fileList = (item.files ?? []) as any;
    this.contractForm.controls['organizationId'].patchValue(item.organization?.id);
    this.contractForm.controls['supplierId'].patchValue(item.supplier?.id);
    this.contractForm.controls['bankId'].patchValue(item.bank?.id);
  }

  private createForm(): void {
    this.contractForm = this.fb.group({
      number: [''],
      date: [''],
      title: [''],
      note: [''],
      supplierId: [null],
      currency: [''],
      dueDays: [null],
      organizationId: [null],
      bankId: [null],
      iban: [''],
    });
  }

  private addContract() {
    const modalRef = this.modal.open(this.templateRef, {size: 'lg'});
    modalRef.result.then(result => {
      this.closeModal(result);
    }, err => {
      this.closeModal(err);
    });
  }
}
